import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, ChevronDown, Eye } from 'lucide-react';
import { getAccessories } from '../services/api';
import { useCart } from '../context/CartContext';
import Notification from '../components/Notification';
import { saveProductCardPosition, restoreProductCardPosition } from '../utils/scrollRestore';

const Accessories = () => {
  const [priceRange, setPriceRange] = useState('all');
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [notification, setNotification] = useState({ isVisible: false, message: '', type: 'success' });
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const accessoriesData = await getAccessories();
        
        console.log('Accessories API Response:', accessoriesData);
        
        const transformedAccessories = accessoriesData.data.map(item => {
          let imageUrl = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80';
          
          if (item.Images && item.Images.length > 0) {
            const image = item.Images[0];
            if (image.formats && image.formats.medium) {
              imageUrl = image.formats.medium.url.startsWith('http') 
                ? image.formats.medium.url 
                : `${process.env.REACT_APP_STRAPI_URL}${image.formats.medium.url}`;
            } else if (image.url) {
              imageUrl = image.url.startsWith('http') 
                ? image.url 
                : `${process.env.REACT_APP_STRAPI_URL}${image.url}`;
            }
          }
          
          const quantity = item.quantity !== undefined ? item.quantity : (item.inStock ? 999 : 0);
          
          return {
            id: item.id,
            name: item.Name || item.name,
            price: item.Price || item.price,
            priceDisplay: `Rs. ${item.Price || item.price}`,
            image: imageUrl,
            description: item.Description || item.description,
            rating: item.rating || 5,
            isBestSeller: item.isBestseller || item.isBestSeller,
            isOnSale: item.isOnSale || false,
            discountPercentage: item.discountPercentage || 0,
            quantity: quantity,
            inStock: quantity > 0 && (item.inStock !== false)
          };
        });

        setAccessories(transformedAccessories);
      } catch (err) {
        setError('Failed to load accessories data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const priceRanges = [
    { id: 'all', name: 'All Prices' },
    { id: 'under-1000', name: 'Under Rs. 1,000' },
    { id: '1000-2000', name: 'Rs. 1,000 - 2,000' },
    { id: '2000-3000', name: 'Rs. 2,000 - 3,000' },
    { id: 'over-3000', name: 'Over Rs. 3,000' }
  ];

  const getPriceValue = (priceNumber) => {
    return priceNumber;
  };

  const filteredAccessories = accessories.filter(item => {
    const priceValue = getPriceValue(item.price);
    let priceMatch = true;
    
    switch (priceRange) {
      case 'under-1000':
        priceMatch = priceValue < 1000;
        break;
      case '1000-2000':
        priceMatch = priceValue >= 1000 && priceValue <= 2000;
        break;
      case '2000-3000':
        priceMatch = priceValue >= 2000 && priceValue <= 3000;
        break;
      case 'over-3000':
        priceMatch = priceValue > 3000;
        break;
      default:
        priceMatch = true;
    }
    
    return priceMatch;
  });

  const handleAddToCart = (item) => {
    addToCart(item, 1);
    setNotification({
      isVisible: true,
      message: `${item.name} has been added to your cart successfully!`,
      type: 'success'
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };


  useEffect(() => {
    if (!loading) {
      restoreProductCardPosition();
    }
  }, [loading, filteredAccessories.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading accessories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-primary text-white px-4 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Accessories - Mughal Ainak Mahal | Eyewear Accessories in Mughalpura, Lahore</title>
        <meta name="description" content="Discover our collection of eyewear accessories including cases, cleaning supplies, chains, and more. Essential accessories for your glasses. Best prices in Mughalpura, Lahore." />
      </Helmet>

      <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Eyewear Accessories
            </h1>
            <p className="text-xl max-w-2xl mx-auto opacity-90">
              Complete your eyewear collection with our essential accessories
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gray-50 rounded-lg p-4 mb-8">
            <div className="block md:hidden">
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setPriceRange('all');
                  }}
                  className="w-full bg-gray-200 text-secondary py-3 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors duration-200"
                >
                  Clear Filter
                </button>

                <div className="relative">
                  <button
                    onClick={() => setIsPriceOpen(!isPriceOpen)}
                    className="w-full flex items-center justify-between bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    <span>{priceRanges.find(range => range.id === priceRange)?.name || 'All Prices'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isPriceOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {priceRanges.map((range) => (
                        <button
                          key={range.id}
                          onClick={() => {
                            setPriceRange(range.id);
                            setIsPriceOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors duration-200 ${
                            priceRange === range.id ? 'bg-primary text-white' : 'text-gray-700'
                          }`}
                        >
                          {range.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="hidden md:flex items-center justify-between">
              <button
                onClick={() => {
                  setPriceRange('all');
                }}
                className="bg-gray-200 text-secondary py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-colors duration-200"
              >
                Clear Filter
              </button>

              <div className="flex items-center space-x-4">
                <div className="relative">
                  <button
                    onClick={() => setIsPriceOpen(!isPriceOpen)}
                    className="flex items-center space-x-2 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    <span>{priceRanges.find(range => range.id === priceRange)?.name || 'All Prices'}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {isPriceOpen && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {priceRanges.map((range) => (
                        <button
                          key={range.id}
                          onClick={() => {
                            setPriceRange(range.id);
                            setIsPriceOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors duration-200 ${
                            priceRange === range.id ? 'bg-primary text-white' : 'text-gray-700'
                          }`}
                        >
                          {range.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
                {filteredAccessories.map((item) => (
                                    <div
                    key={item.id}
                    id={`product-card-${item.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-lg bg-white shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl md:shadow-lg"
                  >
                    <Link
                      to={`/product/${item.id}`}
                      onClick={() => saveProductCardPosition(item.id)}
                      className="block"
                    >
                      <div className="relative aspect-[1.08/1] overflow-hidden bg-white md:aspect-[4/3]">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80';
                          }}
                        />
                        {item.isOnSale && item.discountPercentage > 0 && (
                          <div className="absolute left-px top-px bg-red-500 px-2 py-0.5 text-[11px] font-semibold text-white rounded-tl-md rounded-br-md sm:px-3 sm:py-1 sm:text-sm">
                            {item.discountPercentage}% OFF
                          </div>
                        )}
                        {item.isBestSeller && (
                          <div className="absolute right-px top-px bg-yellow-500 px-1.5 py-0.5 text-[10px] font-semibold text-white rounded-tr-md rounded-bl-md sm:px-3 sm:py-1 sm:text-sm">
                            Best Seller
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="flex flex-1 flex-col px-2 pb-2 pt-1.5 text-center md:px-5 md:pb-5 md:pt-3">

                      <Link
                        to={`/product/${item.id}`}
                        onClick={() => saveProductCardPosition(item.id)}
                        className="block"
                      >
                        <h3 className="line-clamp-2 min-h-[24px] text-[12px] font-bold uppercase leading-tight tracking-[0.04em] text-secondary transition hover:text-primary sm:text-sm md:min-h-[38px] md:text-base md:tracking-[0.08em]">
                          {item.name}
                        </h3>
                      </Link>

                      <div className="mt-0.5 flex min-h-[16px] flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-xs md:mt-1.5 md:min-h-[22px] md:gap-2 md:text-base">
                        {item.originalPrice && item.originalPrice > item.price && (
                          <span className="text-gray-500 line-through">Rs. {item.originalPrice.toLocaleString()}</span>
                        )}
                        <span className="font-bold text-primary">{item.priceDisplay}</span>
                      </div>

                      <div className="mt-1 flex min-h-[14px] items-center justify-center gap-1 text-[10px] leading-none text-gray-600 md:mt-2 md:min-h-[18px] md:text-sm">
                        <span className="tracking-tight text-yellow-400">★★★★★</span>
                        <span>{item.rating || 5} reviews</span>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleAddToCart(item)}
                        className="mt-1 w-full rounded-md bg-primary px-2 py-2 text-xs font-bold uppercase tracking-[0.06em] text-white transition hover:bg-primary-dark md:mt-2 md:px-4 md:py-3 md:text-base md:tracking-[0.18em]"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredAccessories.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No accessories found with the selected price range.</p>
                </div>
              )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary mb-4">
              Essential Accessories for Your Eyewear
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect and maintain your glasses with our quality accessories
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Protection</h3>
              <p className="text-gray-600">Quality cases to keep your glasses safe and scratch-free</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Maintenance</h3>
              <p className="text-gray-600">Cleaning supplies to keep your lenses crystal clear</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-secondary">Style</h3>
              <p className="text-gray-600">Chains and straps to add personality to your frames</p>
            </div>
          </div>
        </div>
      </section>

      <Notification 
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={handleCloseNotification}
      />
    </div>
  );
};

export default Accessories;
